/**
 * 입력 수집.
 *
 * 여기서 나오는 값은 전부 정수로 양자화된다. 마우스 좌표는 픽셀 정수로 자르고,
 * 이동은 8방향으로만 받는다. 연속값이 시뮬레이션에 들어가면 재현이 깨진다.
 */
import { screenDirToWorld } from '../render/turn.ts';
import { ALL_SQUADS, MAX_SQUADS, Order } from '../sim/commands.ts';
import { PathDrag } from './path-drag.ts';
import type { DragLook, PathPoint } from './path-drag.ts';

export class InputState {
  dx = 0;
  dy = 0;
  strike = false;
  /** 장수의 함성(R). 재사용 대기는 시뮬레이션이 본다. */
  roar = false;
  pendingOrder: Order | null = null;
  pendingRally: { x: number; y: number } | null = null;
  clearRally = false;
  /**
   * 전장을 끌어 그린 이동경로. 첫 지점은 `start`다. 루프가 틱마다 비운다.
   * 끄는 중에도 실린다 — 손가락이 가는 대로 부대가 바로 움직여야 그리는 맛이 난다.
   */
  pendingPath: PathPoint[] = [];
  /**
   * 지금 끌고 있는 곳. 카메라가 앞을 보는 데 쓴다. 손가락이 서 있으면(`movedAt`이
   * 오래되면) 카메라도 선다(`lookTarget`).
   */
  drag: DragLook | null = null;
  /** 선택할 분대 비트 마스크. null이면 변화 없음. */
  pendingSelect: number | null = null;
  /** 게임 로직과 무관한 단축키. 루프가 직접 처리한다. */
  requestVerify = false;
  /**
   * 부대를 고르는 중(게임이 서 있다)이면 전장 누르기를 가로챈다(`BattleControls`). 누르면 `done`만 부르고 그 누르기는
   * 먹는다 — 휩쓸기·경로가 되면 안 된다("아무 데나 눌러 계속"). 예전에는 고른 지휘관 곁에서 누르면 경로를 끌었다
   * (끌기 경로는 껐다, Todo8).
   */
  picking: { done: () => void } | null = null;

  private readonly held = new Set<string>();
  /**
   * 가상 조이스틱의 방향. 조이스틱을 쥐고 있는 동안은 키보드보다 앞선다.
   * 둘을 더하지 않는 이유: 키보드로 오른쪽을 누른 채 조이스틱을 왼쪽으로 밀면
   * 0이 되어 장수가 선다. 손에 쥔 쪽 하나만 믿는다.
   */
  private stickDx = 0;
  private stickDy = 0;
  private stickHeld = false;
  /**
   * 전장 끌기를 놓는다. `attach`가 만든다. 전투가 끝나거나 화면을 떠날 때도 불러,
   * 끄는 중이던 손가락의 포인터 캡처를 푼다. 풀지 않으면 숨은 전장 요소가 다음
   * pointerup까지 포인터를 쥐고 있다.
   */
  private releaseStage: (() => void) | null = null;
  /** 떼어낼 때 쓰려고 들고 있는다. 전투마다 새로 붙이므로 정리하지 않으면 쌓인다. */
  private cleanup: (() => void)[] = [];
  /** 전투가 끝난 뒤의 상태. 조작은 받지 않되 검증 키는 살려 둔다. */
  private suspended = false;

  attach(canvasHost: HTMLElement, toWorld: (x: number, y: number) => { x: number; y: number }): void {
    const on = <T extends EventTarget>(
      target: T,
      type: string,
      fn: EventListenerOrEventListenerObject,
    ): void => {
      target.addEventListener(type, fn);
      this.cleanup.push(() => target.removeEventListener(type, fn));
    };

    on(window, 'keydown', ((e: KeyboardEvent) => {
      if (e.repeat) return;
      if (this.suspended) {
        // 전투가 끝나면 조작은 받지 않는다. 다만 결정론 검증은 끝난 뒤에
        // 눌러보는 것이라 여기서 죽이면 안 된다. 기본 동작도 막지 않아야
        // 결과 패널의 버튼이 스페이스로 눌린다.
        if (e.code === 'F8') this.requestVerify = true;
        return;
      }
      this.held.add(e.code);
      this.applyKeys();
      // 숫자키는 분대 선택, ZXCV는 명령이다. 실시간 전투에서
      // 고르고 찍는 흐름이 손에 붙으려면 두 축이 분리돼야 한다.
      const digit = /^Digit([1-9])$/.exec(e.code);
      if (digit) {
        const n = Number(digit[1]);
        if (n <= MAX_SQUADS) this.pendingSelect = 1 << (n - 1);
        return;
      }

      switch (e.code) {
        case 'Digit0':
        case 'Backquote':
          this.pendingSelect = ALL_SQUADS;
          break;
        // 왼쪽부터 대기·대형·공격·후퇴. 화면 아래 명령 버튼과 같은 순서다.
        // 결정론 검증이 쓰던 V를 후퇴에 내주고 검증은 F8로 옮겼다. 네 명령이
        // 손가락 한 줄에 나란히 있는 편이 개발용 단축키보다 중요하다.
        case 'KeyZ':
          this.pendingOrder = Order.Hold;
          break;
        case 'KeyX':
          this.pendingOrder = Order.Formation;
          break;
        case 'KeyC':
          this.pendingOrder = Order.Attack;
          break;
        case 'KeyV':
          this.pendingOrder = Order.Retreat;
          break;
        case 'F8':
          this.requestVerify = true;
          e.preventDefault();
          break;
        case 'KeyQ':
          this.clearRally = true;
          break;
        case 'KeyR':
          this.roar = true;
          break;
        case 'Space':
          this.strike = true;
          e.preventDefault();
          break;
        default:
          break;
      }
    }) as EventListener);

    on(window, 'keyup', ((e: KeyboardEvent) => {
      this.held.delete(e.code);
      this.applyKeys();
    }) as EventListener);

    on(window, 'blur', () => {
      this.held.clear();
      this.applyKeys();
    });

    // 끌지 않고 뗀 마우스 왼쪽 버튼은 휩쓸기(조종하는 사람이 휘두른다), 오른쪽 버튼은 한 점짜리 경로(옛 군기)다.
    // 끌기는 경로를 그리지 않는다(아래 `pointermove`) — 끈 뒤 뗀 것을 휩쓸기로 치지 않으려고 재기만 한다. 손가락
    // 한 번은 아무것도 걸지 않는다 — 장수가 스스로 치므로 휩쓸기를 걸면 쿨다운만 태운다. 끌기를 어떻게 재는지는
    // `PathDrag`에 있다(화면 좌표로 잰다 — 그 까닭도 거기에).
    let pointer: number | null = null;
    const path = new PathDrag();
    const screen = (e: PointerEvent): { x: number; y: number } => {
      const rect = canvasHost.getBoundingClientRect();
      return { x: e.clientX - rect.left, y: e.clientY - rect.top };
    };
    const local = (e: PointerEvent): { x: number; y: number } => {
      const s = screen(e);
      return toWorld(s.x, s.y);
    };
    on(canvasHost, 'pointerdown', ((e: PointerEvent) => {
      if (this.suspended || pointer !== null) return;
      // 고르는 중 전장 누르기는 고르기를 끝낸다. 예전에는 고른 지휘관에서 누르면 경로를 끌었다(끌기 경로는 껐다).
      if (this.picking) {
        e.preventDefault();
        this.picking.done();
        return;
      }
      // 손가락은 여기서 아무것도 걸지 않는다 — 전장에 닿은 손가락은 조이스틱이 쥔다(`BattleControls.grab`).
      // 전에는 손가락도 붙들어 끌었는지를 쟀는데(쓰지는 않았다), 그러면 조이스틱과 손가락을 서로 붙들려 다툰다.
      if (e.pointerType !== 'mouse') return;
      if (e.button === 2) {
        const p = local(e);
        this.pendingRally = { x: Math.round(p.x), y: Math.round(p.y) };
        return;
      }
      if (e.button !== 0) return;
      pointer = e.pointerId;
      canvasHost.setPointerCapture(e.pointerId);
      const s = screen(e);
      path.down(s.x, s.y, toWorld);
      e.preventDefault();
    }) as EventListener);
    // **끌어 그리는 경로는 껐다**(Todo8: "드래그 경로지정 기능은 끄자. 안쓸거 같아"). 끌기는 재기만 한다 — 끌었으면
    // 뗄 때 휩쓸기로 치지 않는다. 부대를 움직이는 길은 이제 부대장을 직접 모는 것이다(`World.controlled`). 오른쪽
    // 누르기의 한 점짜리 경로는 남긴다.
    const discard: PathPoint[] = [];
    on(canvasHost, 'pointermove', ((e: PointerEvent) => {
      if (e.pointerId !== pointer) return;
      const s = screen(e);
      path.move(s.x, s.y, toWorld, performance.now(), discard);
      discard.length = 0;
    }) as EventListener);
    // 어느 길로 놓든 여기로 모인다. 아이디를 먼저 비워야 캡처 해제가 부르는
    // lostpointercapture가 두 번 들어오지 않는다.
    const letGo = (): void => {
      const id = pointer;
      pointer = null;
      path.up();
      this.drag = null;
      if (id !== null && canvasHost.hasPointerCapture(id)) canvasHost.releasePointerCapture(id);
    };
    this.releaseStage = letGo;
    const finish = (e: PointerEvent): void => {
      if (e.pointerId !== pointer) return;
      const tapped = !path.dragging && e.type === 'pointerup' && e.pointerType === 'mouse';
      letGo();
      if (tapped && !this.suspended) this.strike = true;
    };
    on(canvasHost, 'pointerup', finish as EventListener);
    on(canvasHost, 'pointercancel', finish as EventListener);
    on(canvasHost, 'lostpointercapture', finish as EventListener);

    on(canvasHost, 'contextmenu', (e) => e.preventDefault());
  }

  /**
   * 전투가 끝났을 때 부른다. 조작만 멈추고 리스너는 남긴다.
   *
   * 통째로 떼면 결정론 검증 키까지 죽는다. 그건 전투가 끝난 뒤에 눌러보는
   * 기능이라 마지막에 사라지면 안 된다. 반대로 그냥 두면 스페이스가
   * 기본 동작을 막아 결과 패널의 버튼이 눌리지 않는다. 그래서 재운다.
   */
  suspend(): void {
    this.suspended = true;
    this.held.clear();
    this.stickHeld = false;
    this.releaseStage?.();
    this.pendingPath.length = 0;
    this.applyKeys();
  }

  /**
   * 화면을 떠날 때 반드시 부른다.
   *
   * 떼지 않으면 전투마다 리스너가 쌓여 죽은 전투의 처리기가 계속 살아 있는다.
   */
  detach(): void {
    this.releaseStage?.();
    this.releaseStage = null;
    for (const off of this.cleanup) off();
    this.cleanup = [];
    this.held.clear();
    this.stickHeld = false;
    this.applyKeys();
  }

  /** 화면 버튼이 부른다. 키보드와 같은 자리에 싣는다. 전투가 끝났으면 받지 않는다. */
  pressOrder(order: Order): void {
    if (this.suspended) return;
    this.pendingOrder = order;
  }

  pressRoar(): void {
    if (this.suspended) return;
    this.roar = true;
  }

  pressSelect(mask: number): void {
    if (this.suspended) return;
    this.pendingSelect = mask;
  }

  /**
   * 가상 조이스틱. 값은 이미 -1, 0, 1로 양자화돼 들어와야 한다.
   * held가 false면 손을 뗀 것이고, 그때부터 키보드가 다시 방향을 정한다.
   */
  setStick(dx: number, dy: number, held: boolean): void {
    if (this.suspended) held = false;
    this.stickDx = held ? dx : 0;
    this.stickDy = held ? dy : 0;
    this.stickHeld = held;
    this.applyKeys();
  }

  /**
   * 가로 화면인가(`Renderer.rotated`). 그러면 화면이 90도 돌아 있으므로, 조이스틱·키가 준 화면 방향을 월드
   * 방향으로 돌려 싣는다: 화면 오른쪽 = 월드 위(적 쪽), 화면 위 = 월드 왼쪽. 커맨드는 늘 월드 방향이라
   * 리플레이·서버 검증은 화면 방향을 모른다. 끌어 그리는 경로는 화면→월드 변환(`screenToWorld`)이 돌린다.
   */
  rotated = false;

  setRotated(on: boolean): void {
    if (on === this.rotated) return;
    this.rotated = on;
    this.applyKeys();
  }

  private applyKeys(): void {
    let x = 0;
    let y = 0;
    if (this.stickHeld) {
      x = this.stickDx;
      y = this.stickDy;
    } else {
      if (this.held.has('KeyA') || this.held.has('ArrowLeft')) x -= 1;
      if (this.held.has('KeyD') || this.held.has('ArrowRight')) x += 1;
      if (this.held.has('KeyW') || this.held.has('ArrowUp')) y -= 1;
      if (this.held.has('KeyS') || this.held.has('ArrowDown')) y += 1;
    }
    // 화면 (x, y) → 월드 (y, -x). 카메라가 월드를 +90도 돌려 보이므로 그 반대로 돈다(`render/turn.ts`).
    [this.dx, this.dy] = screenDirToWorld(this.rotated, x, y);
  }
}
